import React, { useState, useCallback, useEffect } from "react";
import { Container, Row, Col, Modal } from "reactstrap";
import MetaTags from "react-meta-tags";
import Datatables from "pages/table/datatable";
import handleExportCSV from "pages/utils/handleExportCSV";
import { InputField, SelectField } from "pages/InputFields/InputFields";
import { tableColumns } from "./components/TableColumns";
import StatsCard from "./components/StatsCard";
import FilterBar from "./components/FilterBar";
import ClientDetailView from "./components/ClientDetailView";
import { showSuccessAlert, showErrorAlert } from "pages/utils/Alerts/alertMessages";
import { get, post, put, del } from "helpers/api_helper";

const Company = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("Add"); // "Add" or "Edit"
    const [selectedData, setSelectedData] = useState(null);
    const [viewingClient, setViewingClient] = useState(null);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [formData, setFormData] = useState({ first_name: "", last_name: "", email: "", type: null, status: null, phone: "" });
    const [clientsList, setClientsList] = useState([]);
    const [filterType, setFilterType] = useState(null);
    const [filterStatus, setFilterStatus] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchClients = useCallback(async () => {
        try {
            const res = await get("/superadmin/clients");
            const data = res?.data || res;
            if (data && (Array.isArray(data.users) || Array.isArray(data.entities))) {
                const users = data.users || [];
                const entities = data.entities || [];
                const combined = [
                    ...entities.map((e, idx) => ({
                        id: "e_" + (e.entity_id || idx),
                        full_name: e.contact_person || e.entity_title || "Company Entity",
                        email: e.contact_email || "—",
                        type: e.signup_type ? e.signup_type.toUpperCase() : "Client",
                        status: e.status === 0 ? "Active" : "Inactive",
                        phone: e.contact_number || "",
                        raw: e,
                    })),
                    ...users.map((u, idx) => ({
                        id: "u_" + (u.user_id || idx),
                        full_name: `${u.given_name || ""} ${u.family_name || ""}`.trim() || u.email,
                        email: u.email,
                        type: u.roleId === 1 ? "Super Admin" : u.roleId === 2 ? "Admin" : "User",
                        status: u.user_status === "A" ? "Active" : "Pending",
                        phone: u.phone || "",
                        raw: u,
                    }))
                ];
                setClientsList(combined);
            }
        } catch (e) {
            console.error("Failed to load clients from database", e);
        }
    }, []);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    // --- Handlers ---
    const toggleModal = useCallback(() => {
        setModalOpen(!modalOpen);
        if (modalOpen) setSelectedData(null);
    }, [modalOpen]);

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        const { first_name, last_name, type, status, email, phone } = formData;

        if (!first_name || !email) {
            showErrorAlert("Please fill the required fields (Name & Email).");
            return;
        }

        try {
            if (modalMode === "Edit" && selectedData) {
                await put(`/superadmin/clients/${selectedData.id}`, formData);
                showSuccessAlert("Client updated successfully in database!");
            } else {
                await post("/superadmin/clients", formData);
                showSuccessAlert("Client created successfully in database!");
            }
            setFormData({ first_name: "", last_name: "", email: "", type: null, status: null, phone: "" });
            toggleModal();
            fetchClients();
        } catch (e) {
            showErrorAlert("Failed to save client data to database.");
        }
    };

    const handleAction = async (mode, client = null) => {
        if (mode === "Delete") {
            const idsToDelete = client ? [client.id] : selectedRowIds;
            if (idsToDelete.length === 0) return;
            try {
                for (const id of idsToDelete) {
                    await del(`/superadmin/clients/${id}`);
                }
                showSuccessAlert("Client(s) deleted from database!");
                setSelectedRowIds([]);
                fetchClients();
                if (viewingClient && idsToDelete.includes(viewingClient.id)) {
                    setViewingClient(null);
                }
            } catch (e) {
                showErrorAlert("Failed to delete client.");
            }
            return;
        }

        if (mode === "View") {
            setViewingClient(client);
            return;
        }

        setModalMode(mode);
        setSelectedData(client);

        if (mode === "Edit" && client) {
            const parts = (client.full_name || "").split(' ');
            setFormData({
                first_name: parts[0] || "",
                last_name: parts.slice(1).join(' ') || "",
                email: client.email || "",
                phone: client.phone || "",
                type: { value: client.type, label: client.type },
                status: { value: client.status, label: client.status }
            });
        } else {
            setFormData({ first_name: "", last_name: "", email: "", type: null, status: null, phone: "" });
        }
        setModalOpen(true);
    };

    const filteredClients = clientsList.filter(c => {
        const matchesSearch = !searchTerm ||
            (c.full_name && c.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesType = !filterType || c.type?.toLowerCase() === filterType.value?.toLowerCase();
        const matchesStatus = !filterStatus || c.status?.toLowerCase() === filterStatus.value?.toLowerCase();
        return matchesSearch && matchesType && matchesStatus;
    });

    const isAnySelected = selectedRowIds.length > 0;

    // If a client is selected for full-page viewing, render the dedicated view page
    if (viewingClient) {
        return (
            <React.Fragment>
                <MetaTags>
                    <title>{viewingClient.full_name} | Client Details</title>
                </MetaTags>
                <Container fluid>
                    <div className="dash-page ms-4 me-4 my-4">
                        <ClientDetailView
                            client={viewingClient}
                            onBack={() => setViewingClient(null)}
                            onEdit={(c) => {
                                setViewingClient(null);
                                handleAction("Edit", c);
                            }}
                        />
                    </div>
                </Container>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <MetaTags>
                <title>Clients & Customers</title>
            </MetaTags>
            <Container fluid>
                <div className="dash-page ms-4 me-4">
                    <Row className="align-items-center mb-4">
                        <Col md="4">
                            <h4 className="mb-0 fw-bolder">Client Management</h4>
                        </Col>
                        <Col md="8" className="d-flex justify-content-end gap-2">
                            {isAnySelected && (
                                <>
                                    <button className="btn btn-danger border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                        onClick={() => handleAction("Delete")}>
                                        <i className="bx bx-trash me-1"></i> Delete ({selectedRowIds.length})
                                    </button>
                                    <button className="btn btn-success border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                        onClick={handleExportCSV}>
                                        <i className="bx bx-export me-1"></i> Export All ({selectedRowIds.length})
                                    </button>
                                </>
                            )}
                            <button className="btn btn-primary border px-3 animate__animated animate__fadeIn" style={{ minWidth: "130px" }}
                                onClick={() => handleAction("Add")}>
                                <i className="bx bx-plus me-1"></i> New Client
                            </button>
                        </Col>
                    </Row>

                    <StatsCard clientsList={clientsList} />

                    <FilterBar
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        filterType={filterType}
                        setFilterType={setFilterType}
                        filterStatus={filterStatus}
                        setFilterStatus={setFilterStatus}
                    />

                    {/* Data Table */}
                    <div className="p-3 bg-white shadow-sm border" style={{ borderRadius: "10px" }}>
                        <Datatables
                            columns={tableColumns(handleAction)}
                            showTableOnly={true}
                            rows={filteredClients}
                            keyField={"id"}
                            isCheckbox={true}
                            loading={false}
                            setSelectedRowIds={setSelectedRowIds}
                        />
                    </div>

                    {/* Modal for Add / Edit only */}
                    <Modal isOpen={modalOpen} toggle={toggleModal} size="md" className="custom-modal">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold mb-1">{`${modalMode} ${selectedData ? selectedData.full_name : 'Client'}`}</h5>
                            <button type="button" onClick={toggleModal} className="close" aria-label="Close"></button>
                        </div>

                        <div className="modal-body">
                            <div className="p-2">
                                <Row className="g-3">
                                    <Col md="6">
                                        <InputField label="First Name" name="first_name" type="text"
                                            onChange={handleChange} value={formData.first_name} placeholder="Enter first name" />
                                    </Col>
                                    <Col md="6">
                                        <InputField label="Last Name" name="last_name" type="text"
                                            onChange={handleChange} value={formData.last_name} placeholder="Enter last name" />
                                    </Col>
                                    <Col md="12">
                                        <InputField label="Email" name="email" type="email"
                                            onChange={handleChange} value={formData.email} placeholder="Enter email address" />
                                    </Col>
                                    <Col md="6">
                                        <InputField label="Phone" name="phone" type="text"
                                            onChange={handleChange} value={formData.phone} placeholder="Enter phone number" />
                                    </Col>
                                    <Col md="6">
                                        <SelectField label="Type" name="type" isClearable={true} placeholder="Select Type"
                                            value={formData.type} onChange={(option) => setFormData(prev => ({ ...prev, type: option || null }))}
                                            options={[
                                                { value: "Client", label: "Client" },
                                                { value: "Customer", label: "Customer" },
                                            ]}
                                        />
                                    </Col>
                                    <Col md="12">
                                        <SelectField label="Status" name="status" value={formData.status} isClearable={true} placeholder="Select Status"
                                            onChange={(option) => setFormData(prev => ({ ...prev, status: option || null }))}
                                            options={[
                                                { value: "Active", label: "Active" },
                                                { value: "Inactive", label: "Inactive" },
                                                { value: "Pending", label: "Pending" },
                                                { value: "Archived", label: "Archived" },
                                                { value: "Suspended", label: "Suspended" },
                                            ]}
                                        />
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <button className="btn btn-danger" style={{ width: "100px" }} onClick={toggleModal}>Cancel</button>
                                    <button className="btn btn-primary px-4" onClick={handleSubmit}
                                        style={{ width: `${modalMode === "Add" ? "110px" : "140px"}` }}>
                                        {modalMode === "Add" ? "Create" : "Save Changes"}</button>
                                </div>
                            </div>
                        </div>
                    </Modal>
                </div>
            </Container>
        </React.Fragment>
    );
}

export default Company;
